<template>
    <div class="space-y-4" v-if="!isLoading">
        <CategoryBreadcrumbs></CategoryBreadcrumbs>
        <h1
            class="pt-4 text-2xl font-semibold tracking-tight flex justify-between items-center gap-2"
        >
            <div class="flex items-center">
                <span>{{ bank }}</span>
                <div class="flex items-center gap-1 pl-3">
                    <Pill backgroundColor="green-50 border border-green-200"
                        >{{ catStats?.published ?? "0" }} Published</Pill
                    >
                    <Pill backgroundColor="yellow-50 border border-yellow-200"
                        >{{ catStats?.draft ?? "0" }} Draft</Pill
                    >
                    <Pill backgroundColor="red-50 border border-red-200"
                        >{{ catStats?.closed ?? "0" }} Closed</Pill
                    >
                </div>
            </div>
            <div>
                <button
                    class="main button small group rounded-md"
                    @click="handleExport"
                >
                    Export bank
                </button>
            </div>
        </h1>
        <div class="mt-8" v-if="categoryCounts && categoryCounts.length">
            <div class="mb-3 flex items-center justify-between gap-2">
                <button
                    type="button"
                    class="flex items-center gap-1.5 text-lg font-semibold tracking-tight"
                    @click="showCategoryCounts = !showCategoryCounts"
                >
                    <ChevronRightIcon
                        class="h-5 w-5 transition-transform"
                        :class="{ 'rotate-90': showCategoryCounts }"
                    />
                    Questions by category
                </button>
                <button
                    v-if="showCategoryCounts"
                    type="button"
                    class="text-sm font-medium text-blue-600 hover:text-blue-800"
                    @click="allExpanded ? collapseAll() : expandAll()"
                >
                    {{ allExpanded ? "Collapse all" : "Expand all" }}
                </button>
            </div>
            <BankCategoryCountTree
                v-if="showCategoryCounts"
                :categories="categoryCounts"
                :bank-id="bankId"
                :expanded="expandedCategories"
            />
        </div>
        <div class="mt-10 space-y-4">
            <ContentQuestionFilter v-model="filters"></ContentQuestionFilter>
            <PaginationManager
                :pagination-data="questionsPage"
                @next="
                    () => {
                        page++;
                    }
                "
                @prev="
                    () => {
                        page--;
                    }
                "
                v-if="questionsPage?.data"
            ></PaginationManager>
            <ContentQuestionsList
                :questions="questionsPage?.data"
                questionType="prep"
                v-if="questionsPage?.data"
            />
            <PaginationManager
                :pagination-data="questionsPage"
                @next="
                    () => {
                        page++;
                    }
                "
                @prev="
                    () => {
                        page--;
                    }
                "
                v-if="questionsPage?.data"
            ></PaginationManager>
        </div>
    </div>
    <div v-else>
        <h1>Loading...</h1>
    </div>
</template>

<script setup lang="tsx">
/**
 * Component to display a question bank category page
 */

import { computed, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useQuery } from "@tanstack/vue-query";
import { adminApi } from "@/api/admin";
import type { BankCategoryCount } from "@/types";
import ContentQuestionsList from "@/components/Admin/Content/questions/ContentQuestionsList.vue";
import PaginationManager from "@/components/PaginationManager.vue";
import ContentQuestionFilter, {
    FilterOptions,
} from "../Content/ContentQuestionFilter.vue";
import { CategoryBreadcrumbs } from "./CategoryBreadcrumbs.tsx";
import BankCategoryCountTree from "./BankCategoryCountTree.vue";
import { ChevronRightIcon } from "@heroicons/vue/20/solid";
import axios from "axios";
import $events from "@/event-bus.ts";

const filters = ref<FilterOptions>({
    access: "open",
    flagged: "all",
    keyword: "",
    status: "published",
});

const showCategoryCounts = ref(true);

const route = useRoute();
const bankId = computed(() => parseInt(route.params.id as string));
const categoryId = computed(() => parseInt(route.params.categoryId as string));

const page = ref(1);
const { data: questionsPage, isLoading } = useQuery({
    queryKey: [
        "bank",
        bankId,
        "category",
        categoryId,
        "questions",
        page,
        filters,
    ],
    queryFn: async () => {
        const response = await adminApi.questionBank.searchQuestions({
            deep: true,
            page: page.value,
            ...filters.value,
            bankId: bankId.value,
        });
        return response.data;
    },
});

const { data: catStats } = useQuery({
    queryKey: ["bank", bankId, "category", categoryId, "stats"],
    queryFn: async () => {
        const response = await adminApi.questionBank.categoryStats(
            bankId.value,
            categoryId.value
        );
        return response.data;
    },
});

const { data: categoryCounts } = useQuery({
    queryKey: ["bank", bankId, "category-counts"],
    queryFn: async () => {
        const response = await adminApi.questionBank.categoryCounts(
            bankId.value
        );
        return response.data;
    },
});

// Shared expand state for the category tree, keyed by category id, so the
// whole tree can be expanded/collapsed from one control.
const expandedCategories = reactive<Record<number, boolean>>({});

const collectParentIds = (nodes: BankCategoryCount[]): number[] =>
    nodes.flatMap((node) =>
        node.children && node.children.length
            ? [node.id, ...collectParentIds(node.children)]
            : []
    );

const parentIds = computed(() =>
    categoryCounts.value ? collectParentIds(categoryCounts.value) : []
);

const allExpanded = computed(
    () =>
        parentIds.value.length > 0 &&
        parentIds.value.every((id) => expandedCategories[id])
);

const expandAll = (): void => {
    parentIds.value.forEach((id) => (expandedCategories[id] = true));
};

const collapseAll = (): void => {
    parentIds.value.forEach((id) => (expandedCategories[id] = false));
};

// Default newly-seen categories to expanded while preserving any manual
// toggles across background refetches.
watch(
    parentIds,
    (ids) => {
        ids.forEach((id) => {
            if (!(id in expandedCategories)) expandedCategories[id] = true;
        });
    },
    { immediate: true }
);

const { data: bank } = useQuery({
    queryKey: ["bank", bankId],
    queryFn: async () => {
        const response = await adminApi.questionBank.getBank(bankId.value);
        return response?.name ?? "";
    },
});

const handleExport = async () => {
    try {
        await axios.post(`/api/prep/question-banks/export/${bank.value}`);
        $events.$emit("toastNotification", {
            action: "Success",
            heading: "Export started!",
            message:
                "You'll receive an email when the export process is complete.",
            status: "success",
        });
    } catch (e: any) {
        $events.$emit("toastNotification", {
            action: "Failure",
            heading: `Export failed: ${e?.error}`,
            message:
                e?.message ??
                `Something went wrong attempting to export the ${bank.value} bank. Please contact someone on the dev team.`,
            status: "failure",
        });
    }
};

watch(categoryId, () => {
    page.value = 1;
});
</script>
